import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import httpRequest from "../../../utils/Http";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BookingRecentActivityTab = ({ bookingHistory = {}, setBookingHistory, bid }) => {
  const currentUser = useSelector((state) => state.authReducer.currentUser);
  const [commentText, setCommentText] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activityDates = useMemo(() => Object.keys(bookingHistory).sort((a, b) => new Date(b) - new Date(a)), [bookingHistory]);
  const activityCount = useMemo(() => activityDates.reduce((total, date) => total + (bookingHistory[date]?.length || 0), 0), [activityDates, bookingHistory]);

  const submitComment = async (event) => {
    event.preventDefault();
    const comment = commentText.trim();
    if (!comment) {
      setFormError("Write a comment before posting.");
      return;
    }
    setFormError("");
    setIsSubmitting(true);
    try {
      const response = await httpRequest("addcomment", { bid, comment }, "POST");
      if (response.status) {
        setBookingHistory(response.bookingHistory);
        setCommentText("");
        toast.success("Comment added");
      } else toast.error(response.message);
    } catch {
      toast.error("Unable to add the comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getActor = (activity) => {
    const agent = activity.agent_detail || {};
    return { name: agent.name || "System", organisation: agent.service_centre?.display_name || agent.partner?.display_name };
  };

  const sortNewestActivityFirst = (first, second) => {
    const idDifference = Number(second.id) - Number(first.id);
    if (!Number.isNaN(idDifference) && idDifference !== 0) return idDifference;
    return new Date(second.createdAt) - new Date(first.createdAt);
  };

  return (
    <section className="booking-activity-feed">
      <header className="booking-activity-feed__header">
        <div><span>Booking timeline</span><h2>Recent activity</h2><p>Updates, actions, and comments for this booking.</p></div>
        <span className="booking-activity-feed__count">{activityCount} {activityCount === 1 ? "update" : "updates"}</span>
      </header>

      {activityDates.length ? <div className="booking-activity-feed__groups">{activityDates.map((date) => <section className="booking-activity-day" key={date}><h3><time dateTime={date}>{moment(date).calendar(null, { sameDay: "[Today]", lastDay: "[Yesterday]", lastWeek: "dddd", sameElse: "DD MMM YYYY" })}</time><span>{moment(date).format("DD MMM YYYY")}</span></h3><ol>{[...(bookingHistory[date] || [])].sort(sortNewestActivityFirst).map((activity, index) => { const actor = getActor(activity); return <li key={`${activity.id || activity.createdAt}-${index}`}><span className="booking-activity-day__marker"><i className="fas fa-bolt" aria-hidden="true" /></span><article><div className="booking-activity-day__top"><strong>{activity.action || "Booking updated"}</strong><time>{activity.createdAt ? moment(activity.createdAt).format("hh:mm A") : "—"}</time></div><p className="booking-activity-day__actor">{actor.name}{actor.organisation && ` · ${actor.organisation}`}</p>{activity.remarks && <p className="booking-activity-day__remarks">{activity.remarks}</p>}</article></li>; })}</ol></section>)}</div> : <div className="booking-activity-feed__empty"><i className="fas fa-stream" aria-hidden="true" /><h3>No activity yet</h3><p>Updates and comments added to this booking will appear here.</p></div>}

      <form className="booking-activity-composer" onSubmit={submitComment}>
        <div className="booking-activity-composer__avatar" aria-hidden="true">{(currentUser?.name || "U").charAt(0).toUpperCase()}</div>
        <div className="booking-activity-composer__input"><label htmlFor="booking-comment">Add a note</label><textarea id="booking-comment" value={commentText} onChange={(event) => { setCommentText(event.target.value); setFormError(""); }} placeholder="Share an update with the team…" rows="3" /></div>
        <div className="booking-activity-composer__actions">{formError && <span className="booking-activity-composer__error">{formError}</span>}<button type="submit" disabled={isSubmitting}>{isSubmitting ? "Posting…" : "Post note"}<i className="fas fa-arrow-up" aria-hidden="true" /></button></div>
      </form>
    </section>
  );
};

export default BookingRecentActivityTab;
